
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

interface BookingSubmissionData {
  meeting_link_id: string;
  booker_name: string;
  booker_email: string;
  booker_company?: string;
  message?: string;
  booking_date: string;
  timezone?: string;
  guests?: Array<{ name?: string; email: string; phone?: string }>;
  responses?: Array<{ question_id: string; response_value: string }>;
  businessSlug?: string;
  customRedirectUrl?: string;
}

// Track in-flight booking requests to prevent duplicates
const pendingBookings = new Map<string, Promise<any>>();
// Track events that have been fired to prevent duplicates
const trackedEvents = new Set<string>();

export function useBookingSubmission() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const location = useLocation();
  const queryClient = useQueryClient();

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Function to safely track events without duplicates
  const safeTrackEvent = useCallback((eventName: string, properties: any) => {
    // Create a unique event ID to prevent duplicates
    const eventId = `${eventName}_${JSON.stringify(properties)}`;
    
    // Only track if we haven't tracked this exact event recently
    if (!trackedEvents.has(eventId)) {
      trackedEvents.add(eventId);
      
      // Remove from tracked events after a delay to allow for future tracking of similar events
      setTimeout(() => {
        trackedEvents.delete(eventId);
      }, 10000); // 10 seconds
      
      // Track the event
      trackEvent({
        name: eventName as any,
        properties
      });
    }
  }, []);

  const processQuestionResponses = useCallback((formData: any) => {
    // Extract custom question responses from form data
    const responses: any[] = [];
    
    Object.keys(formData).forEach(key => {
      if (key.startsWith("question_")) {
        const questionId = key.replace("question_", "");
        const responseValue = Array.isArray(formData[key]) 
          ? formData[key].join(", ") // Convert arrays to comma-separated strings
          : formData[key];
        
        responses.push({
          question_id: questionId,
          response_value: responseValue
        });
      }
    });
    
    return responses;
  }, []);

  const submitBooking = async (data: BookingSubmissionData) => {
    console.log('🚀 [useBookingSubmission] submitBooking called with data:', data);
    console.log('✅ [submitBooking] Proceeding with booking submission...');
    setLoading(true);
    setError(null);
    
    // Safety check for component mount state
    const isMounted = () => mountedRef.current;

    // TEMPORARILY SKIP CALENDAR VALIDATION - Calendar is connected but validation is failing
    console.log('🔍 [submitBooking] SKIPPING calendar validation temporarily...');
    try {
      console.log('🔍 [submitBooking] Fetching meeting link for ID:', data.meeting_link_id);
      const { data: meetingLink, error: linkError } = await supabase
        .from('meeting_links')
        .select('user_id')
        .eq('id', data.meeting_link_id)
        .single();

      if (linkError || !meetingLink) {
        console.log('❌ [submitBooking] Meeting link error:', linkError);
        throw new Error('Meeting link not found');
      }

      console.log('✅ [submitBooking] Meeting link found, user_id:', meetingLink.user_id);
      console.log('⚠️ [submitBooking] Calendar validation bypassed - proceeding with booking');
    } catch (calendarError: any) {
      console.error('[submitBooking] Meeting link validation failed:', calendarError);
      if (isMounted()) setError(calendarError.message);
      if (isMounted()) setLoading(false);
      if (isMounted()) {
        toast({
          title: "Booking Unavailable",
          description: calendarError.message,
          variant: "destructive"
        });
      }
      return { success: false, error: calendarError.message };
    }
    
    // Create a unique booking request ID to prevent duplicate submissions
    const bookingRequestId = `${data.meeting_link_id}_${data.booking_date}_${data.booker_email}`;
    
    // Check if this exact booking is already being processed
    if (pendingBookings.has(bookingRequestId)) {
      console.log(`[submitBooking] Duplicate booking request detected: ${bookingRequestId}`);
      try {
        // Wait for the existing request to complete
        const result = await pendingBookings.get(bookingRequestId);
        setLoading(false);
        return result;
      } catch (err: any) {
        console.error('[submitBooking] Error from existing booking request:', err);
        setError("Failed to create booking");
        setLoading(false);
        return { success: false, error: err.message };
      }
    }
    
    try {
      // Update URL with form data if available
      if (data.booker_name && data.booker_email) {
        const urlParams = new URLSearchParams(location.search);
        urlParams.set('name', data.booker_name);
        urlParams.set('email', data.booker_email);
        if (data.booker_company) urlParams.set('company', data.booker_company);
        
        // Update URL without triggering navigation/reload
        window.history.replaceState(
          null, 
          '', 
          `${location.pathname}?${urlParams.toString()}`
        );
      }
      
      // Process any custom question responses
      const customQuestionResponses = data.responses || processQuestionResponses(data);
      console.log('🔍 [submitBooking] Custom question responses:', customQuestionResponses);
      
      // Create a promise for this booking request that can be reused
      const bookingPromise = new Promise<any>(async (resolve, reject) => {
        try {
          console.log("[submitBooking] Creating booking with data:", {
            meeting_link_id: data.meeting_link_id,
            booker_name: data.booker_name,
            booker_email: data.booker_email,
            guests: data.guests
          });
          
          console.log("[DEBUG] About to call create-booking edge function...");
          console.log("[DEBUG] Payload:", {
            meeting_link_id: data.meeting_link_id,
            booker_name: data.booker_name,
            booker_email: data.booker_email,
            timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
          });
          
          console.log("🚀 [submitBooking] CALLING SUPABASE FUNCTION NOW...");
          console.log("🚀 [submitBooking] supabase object:", !!supabase);
          console.log("🚀 [submitBooking] supabase.functions:", !!supabase.functions);
          
          // Call the Supabase edge function using functions.invoke
          const requestBody = {
            meeting_link_id: data.meeting_link_id,
            booker_name: data.booker_name,
            booker_email: data.booker_email,
            booker_company: data.booker_company || null,
            message: data.message || null,
            booking_date: data.booking_date,
            timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            guests: data.guests || [],
            custom_question_responses: customQuestionResponses
          };
          
          console.log('🚀 [submitBooking] Request body being sent:', JSON.stringify(requestBody, null, 2));
          
          const { data: response, error: fnError } = await supabase.functions.invoke('create-booking', {
            body: requestBody
          });
          
          console.log("[DEBUG] Edge function response received:", { response, fnError });
          console.log("[DEBUG] Response details:", JSON.stringify(response, null, 2));
          
          if (fnError) {
            console.error("Edge function error:", fnError);
            throw new Error(`Failed to create booking: ${fnError.message}`);
          }
          
          if (!response || !response.success) {
            console.error("API response error:", response?.error || "Unknown error");
            throw new Error(response?.error || "Failed to create booking");
          }
          
          // Track successful booking
          if (data.businessSlug) {
            safeTrackEvent("booking_completed", {
              meetingLinkId: data.meeting_link_id,
              bookingId: response.bookingId || response.data?.id,
              businessSlug: data.businessSlug,
              guestCount: data.guests ? data.guests.length : 0
            });
          }
          
          // Handle redirect
          const result = data.customRedirectUrl 
            ? { success: true, redirectUrl: data.customRedirectUrl }
            : { success: true, redirectUrl: `/book/success?id=${response.bookingId || response.data?.id}`, ...response };
            
          resolve(result);
        } catch (err) {
          reject(err);
        } finally {
          // Remove from pending bookings after a short delay
          setTimeout(() => {
            pendingBookings.delete(bookingRequestId);
          }, 5000);
        }
      });
      
      // Store this promise to prevent duplicate submissions
      pendingBookings.set(bookingRequestId, bookingPromise);
      
      // Wait for booking to complete
      const result = await bookingPromise;
      
      // Invalidate calendar slots cache to refresh availability
      await queryClient.invalidateQueries({ 
        queryKey: ['calendar-slots'] 
      });
      console.log('[submitBooking] Invalidated calendar-slots cache after successful booking');
      
      // Toast message removed as requested
      
      // Handle redirect if needed
      if (result.redirectUrl && data.customRedirectUrl) {
        window.location.href = data.customRedirectUrl;
      }
      
      return result;
      
    } catch (error: any) {
      console.error("Booking error:", error);
      
      // Only update state if component is still mounted
      if (mountedRef.current) {
        setError("Failed to create booking");
        
        toast({
          title: "Booking Failed",
          description: "Please try again or contact support if the issue persists.",
          variant: "destructive"
        });
      }
      
      if (data.businessSlug) {
        safeTrackEvent("booking_error", {
          meetingLinkId: data.meeting_link_id,
          businessSlug: data.businessSlug,
          error: error.message
        });
      }
      
      return { success: false, error: error.message };
    } finally {
      // Only update loading state if component is still mounted
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  return {
    submitBooking,
    loading,
    error
  };
}
